import { useState, useImperativeHandle, forwardRef, useEffect } from 'react';
import { FS } from "@plannel/utils/font-util";

// 전역 상태 관리
class FilterInfoStore {
    constructor() {
        this.listeners = new Set();
        this.value = null;
    }

    setValue(newValue) {
        this.value = newValue;
        this.listeners.forEach(listener => listener(newValue));
    }

    subscribe(listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    getValue() {
        return this.value;
    }

    // 초기화 메서드 추가
    clear() {
        this.setValue(null);
    }
}

const storeRegistry = new Map();

export const getFilterStore = (viewName = 'default') => {
    if (!storeRegistry.has(viewName)) {
        storeRegistry.set(viewName, new FilterInfoStore());
    }
    return storeRegistry.get(viewName);
};

// 공통 필터 정보 추출 함수
export const extractFilterInfo = (params, t, gridRef = null) => {
    const row = params.data || {};

    // 헤더 이름 가져오기
    const getHeaderName = (colIds=null) => {
        if (gridRef?.current) {
            try {
                let allColumns = [];

                if (gridRef.current.api) {
                    allColumns = gridRef.current.api.getColumns?.() ||
                        gridRef.current.api.getAllDisplayedColumns?.() || [];
                }

                const column = allColumns.find(col => {
                    const colDef = col.getColDef ? col.getColDef() : col;
                    return colIds.includes(colDef.colId);
                });

                if (column) {
                    const colDef = column.getColDef ? column.getColDef() : column;
                    return colDef.headerName || '';
                }
            } catch (e) {
                console.warn('Failed to get header name from gridRef:', e);
            }
        }
        return null;
    };

    // Item
    const itemCdHeader = getHeaderName(['itemCdGrp', 'itemHrchyCdGrp', 'hrchyCdGrp', 'itemCdId']);
    const itemNmHeader = getHeaderName(['itemNmGrp', 'itemHrchyNmGrp', 'hrchyNmGrp', 'productlevel']);

    // Customer
    const customerCdHeader = getHeaderName(['custCdGrp', 'custHrchyCdGrp', 'customerCd', 'customerCdGrp', 'custCdId']);
    const customerNmHeader = getHeaderName(['custNmGrp', 'custHrchyNmGrp', 'customerNm', 'customerNmGrp', 'custlevel']);

    // Origin Location
    const originLocCdHeader = getHeaderName(['originLocCd', 'originLocCdGrp', 'locCdGrp', 'locCodeGrp']);
    const originLocNmHeader = getHeaderName(['originLocNm', 'originLocNmGrp', 'locNmGrp', 'locNameGrp', 'LOC_GRP_NMignoreP13nColId']);

    // Dest Location
    const destLocCdHeader = getHeaderName(['destLocCd', 'destLocCdGrp']);
    const destLocNmHeader = getHeaderName(['destLocNm', 'destLocNmGrp']);

    // Work Center
    const wcCdHeader = getHeaderName(['wcCd', 'wcCdGrp']);
    const wcNmHeader = getHeaderName(['wcNm', 'wcNmGrp']);

    // Resource
    const resCdHeader = getHeaderName(['resCd', 'resCdGrp']);
    const resNmHeader = getHeaderName(['resNm', 'resNmGrp']);

    return {
        // Item
        itemCd: row.itemCd ?? row.itemHrchyCd ?? null,
        itemNm: row.itemNm ?? row.itemName ?? row.itemHrchyNm ?? row.productlevelId ?? null,
        itemCdHeader,
        itemNmHeader,

        // Customer
        customerCd: row.customerCd ?? row.custCd ?? null,
        customerNm: row.customerNm ?? row.custNm ?? row.custlevelId ?? null,
        customerCdHeader,
        customerNmHeader,

        // Origin Location (Location)
        originLocCd: row.originLocCd ?? row.vendorLocCd ?? row.locCd ?? null,
        originLocNm: row.originLocNm ?? row.vendorLocNm ?? row.locNm ?? row.locName ?? null,
        originLocCdHeader,
        originLocNmHeader,

        // Dest
        destLocCd: row.destLocCd ?? null,
        destLocNm: row.destLocNm ?? null,
        destLocCdHeader,
        destLocNmHeader,

        // Work Center
        workCenterCd: row.wcCd ?? null,
        workCenterNm: row.wcNm ?? null,
        workCenterCdHeader: wcCdHeader,
        workCenterNmHeader: wcNmHeader,

        // Resource
        resourceCd: row.resCd ?? null,
        resourceNm: row.resNm ?? null,
        resourceCdHeader: resCdHeader,
        resourceNmHeader: resNmHeader,
    };
};


const FilterStatusPanel = forwardRef((props, ref) => {
    const [filterInfo, setFilterInfo] = useState(null);
    const viewName = props.viewName || 'default';

    useImperativeHandle(ref, () => ({}));

    useEffect(() => {
        const store = getFilterStore(viewName);
        setFilterInfo(store.getValue());

        const unsubscribe = store.subscribe(setFilterInfo);
        return unsubscribe;
    }, [viewName]);

    if (!filterInfo) {
        return null;
    }

    const filterItems = [];

    // Origin Location
    if (filterInfo.originLocCd && filterInfo.originLocCdHeader) {
        filterItems.push({
            label: filterInfo.originLocCdHeader,
            value: filterInfo.originLocCd
        });
    }
    if (filterInfo.originLocNm && filterInfo.originLocNmHeader) {
        filterItems.push({
            label: filterInfo.originLocNmHeader,
            value: filterInfo.originLocNm
        });
    }

    // Dest Location
    if (filterInfo.destLocCd && filterInfo.destLocCdHeader) {
        filterItems.push({
            label: filterInfo.destLocCdHeader,
            value: filterInfo.destLocCd
        });
    }
    if (filterInfo.destLocNm && filterInfo.destLocNmHeader) {
        filterItems.push({
            label: filterInfo.destLocNmHeader,
            value: filterInfo.destLocNm
        });
    }

    // Customer
    if (filterInfo.customerCd && filterInfo.customerCdHeader) {
        filterItems.push({
            label: filterInfo.customerCdHeader,
            value: filterInfo.customerCd
        });
    }
    if (filterInfo.customerNm && filterInfo.customerNmHeader) {
        filterItems.push({
            label: filterInfo.customerNmHeader,
            value: filterInfo.customerNm
        });
    }

    // Item
    if (filterInfo.itemCd && filterInfo.itemCdHeader) {
        filterItems.push({
            label: filterInfo.itemCdHeader,
            value: filterInfo.itemCd
        });
    }
    if (filterInfo.itemNm && filterInfo.itemNmHeader) {
        filterItems.push({
            label: filterInfo.itemNmHeader,
            value: filterInfo.itemNm
        });
    }

    // Work Center
    if (filterInfo.workCenterCd && filterInfo.workCenterCdHeader) {
        filterItems.push({
            label: filterInfo.workCenterCdHeader,
            value: filterInfo.workCenterCd
        });
    }
    if (filterInfo.workCenterNm && filterInfo.workCenterNmHeader) {
        filterItems.push({
            label: filterInfo.workCenterNmHeader,
            value: filterInfo.workCenterNm
        });
    }

    // Resource
    if (filterInfo.resourceCd && filterInfo.resourceCdHeader) {
        filterItems.push({
            label: filterInfo.resourceCdHeader,
            value: filterInfo.resourceCd
        });
    }
    if (filterInfo.resourceNm && filterInfo.resourceNmHeader) {
        filterItems.push({
            label: filterInfo.resourceNmHeader,
            value: filterInfo.resourceNm
        });
    }

    if (filterItems.length === 0) {
        return null;
    }

    return (
        <div style={{
            padding: '0',
            display: 'flex',
            flexWrap: 'wrap',  // 자동 줄바꿈
            alignItems: 'center',
            gap: '5px',
            height: '100%',
            maxHeight: '48px',  // 최대 2줄 높이
            overflow: 'hidden',  // 2줄 넘으면 숨김
            boxSizing: 'border-box',
            margin: 'auto 0'
        }}>
            {filterItems.map((item, _) => (
                <span
                    key={`${item.label}-${item.value}`}
                    title={`${item.label}: ${item.value}`}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        fontSize: FS.BODY,
                        whiteSpace: 'nowrap',
                        height: '20px',
                        flexShrink: 0
                    }}
                >
                    <span style={{
                        padding: '2px 6px',
                        backgroundColor: '#e3f2fd',
                        color: '#1976d2',
                        borderRadius: '3px 0 0 3px',
                        fontWeight: '500',
                        fontSize: FS.CAPTION,
                        display: 'inline-flex',
                        alignItems: 'center',
                        height: '100%'
                    }}>
                        {item.label}
                    </span>
                    <span style={{
                        padding: '2px 6px',
                        backgroundColor: '#f5f5f5',
                        color: '#000',
                        borderRadius: '0 3px 3px 0',
                        fontWeight: '600',
                        borderLeft: '1px solid #ddd',
                        display: 'inline-flex',
                        alignItems: 'center',
                        height: '100%',
                        maxWidth: '200px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    }}>
                        {item.value}
                    </span>
                </span>
            ))}
        </div>
    );
});

export default FilterStatusPanel;
